import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';

applyPlugin(jsPDF);

// ===== YAE Brand palette (RGB tuples for jsPDF) =====
const COLOR = {
  ink: [26, 10, 34],
  plum: [61, 26, 77],
  plumDeep: [42, 15, 58],
  orange: [232, 84, 42],
  cream: [245, 241, 234],
  paper: [250, 247, 242],
  paper2: [239, 234, 224],
  muted: [107, 92, 112],
  rule: [220, 215, 208],
  white: [255, 255, 255],
  black: [0, 0, 0]
};

// Question mappings for all sections
const questionMappings = {
  'A1.1': 'Does your organisation operate within India (registered or functional entity)?',
  'A1.2': 'Do you process personal data of individuals located in India (customers, employees, contractors)?',
  'A1.3': 'Do you process personal data digitally or intend to digitise physical records?',
  'A1.4': 'Do you use third-party IT systems or SaaS tools that store/process personal data?',
  'A1.5': 'Do you transfer personal data outside India through cloud services or operational workflows?',

  'B1.1': 'Basic identifiers (Name, Phone, Email, Address)',
  'B1.2': 'Financial data',
  'B1.3': 'Government IDs (PAN, Aadhaar, Passport)',
  'B1.4': 'Employment data',
  'B1.5': 'Health data',
  'B1.6': 'Biometric data',
  'B1.7': 'Behavioural data',
  'B1.1.8': 'Location data',
  'B1.8': 'Do you collect or process data of minors (below 18 years)?',
  'B1.9': 'Do you collect sensitive data (financial, health, biometrics, Aadhaar)?',
  'B1.10': 'Do you process large volumes of customer or employee data (threshold: >10,000 individuals per year)?',
  'B1.9A': 'Do you have a Data Protection Officer (DPO) or equivalent role?',
  'B1.9B': 'Do you have a formalised data governance framework?',

  'C1.1': 'Do you obtain explicit, itemised consent before collecting personal data?',
  'C1.2': 'Are consent notices written in simple, plain English or local language?',
  'C1.3': 'Can individuals easily withdraw consent at any time?',
  'C1.4': 'Do you maintain consent logs (timestamp, purpose, user identity)?',
  'C1.5': 'Are notices updated whenever processing purpose changes?',
  'C2.1': 'Do you have an established workflow for responding to user rights requests (access, correction, erasure, grievance)?',
  'C2.2': 'Do you track turnaround times for grievance redressal (requirements: 7-30 days)?',
  'C2.3': 'Is there a designated Data Protection Manager (DPM) or grievance officer?',

  'D1.1': 'Do you collect only data strictly required for your services?',
  'D1.2': 'Do you prevent over-collection of unnecessary customer information?',
  'D2.1': 'Do you have a documented data retention policy?',
  'D2.2': 'Do you securely delete or anonymise data after retention period expires?',
  'D2.3': 'Do you periodically review and update retention periods?',

  'E1.1': 'Do you implement access controls (role-based access, least privilege)?',
  'E1.2': 'Do you encrypt data in transit (HTTPS, TLS)?',
  'E1.3': 'Do you implement endpoint security (device encryption, anti-malware)?',
  'E1.4': 'Do you encrypt data at rest on servers/databases?',
  'E1.5': 'Do you use multi-factor authentication (MFA) for admin access?',
  'E2.1': 'Do you conduct regular vulnerability assessments and penetration testing?',
  'E2.2': 'Do you have a documented breach management SOP?',
  'E2.3': 'Do you assess vendor security practices before onboarding?',
  'E2.4': 'Do you monitor systems for security incidents?',

  'F1': 'Do you share personal data with third-party vendors or processors?',
  'F2': 'Do you use SaaS tools that process personal data?',
  'F3': 'Do you have Data Sharing Agreements (DSA) with all vendors?',
  'F4': 'Do you conduct vendor security audits?',
  'F5': 'Do you use free or trial versions of tools that process personal data?',

  'G1': 'Do you transfer personal data outside India?',
  'G2': 'Do you transfer to countries not on the approved list?',
  'G3': 'Do you have adequate safeguards (contractual clauses, adequacy decisions)?',

  'H1': 'Do you provide privacy training to employees handling personal data?',
  'H2': 'Do you conduct privacy awareness programs?',
  'H3': 'Do you maintain training logs?',

  'I1.1': 'Privacy Policy',
  'I1.2': 'Data Retention Policy',
  'I1.3': 'Consent Management Policy',
  'I1.4': 'Breach Management SOP',
  'I1.5': 'Vendor Risk Policy',
  'I1.6': 'Data Accuracy & Correction SOP',
  'I2': 'Is top management aware of DPDP Act obligations?',
  'I3': 'Is there Board-level or CXO-level oversight for privacy?',

  'J1.1': 'Do you operate in financial services (banking, insurance, fintech)?',
  'J1.2': 'Do you handle payment card data (PCI DSS scope)?',
  'J1.3': 'Do you process credit scores or financial profiles?',
  'J1.4': 'Do you share financial data with third parties?',
  'J1.5': 'Do you have explicit consent for financial data sharing?',
  'J2.1': 'Do you operate in healthcare (hospitals, clinics, healthtech)?',
  'J2.2': 'Do you share health records with external parties?',
  'J2.3': 'Is health data sharing done without proper safeguards?',
  'J3.1': 'Do you process employee personal data (HR systems)?',
  'J3.2': 'Do you conduct background checks or employee screening?',
  'J3.3': 'Do you share employee data with third-party HR vendors?',
  'J4.1': 'Do you operate in EdTech (schools, online learning platforms)?',
  'J4.2': 'Do you collect data from minors (students under 18)?',
  'J5.1': 'Do you store delivery addresses and payment logs?',
  'J5.2': 'Do you store browsing and purchase patterns?'
};

const getQuestionText = (key) => questionMappings[key] || key;

const formatAnswer = (value, isCheckbox = false) => {
  if (isCheckbox) return value === true ? 'Selected' : 'Not selected';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not answered';
};

// Sanitize unicode characters that core PDF fonts can't render
const safe = (s) => {
  if (s === null || s === undefined) return '';
  return String(s)
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2026]/g, '...');
};

// Risk colors aligned to brand
const riskColors = {
  'Low Risk':      [0, 130, 80],
  'Moderate Risk': [184, 134, 11],
  'High Risk':     COLOR.orange,
  'Critical Risk': [127, 29, 29]
};

// Draw an outlined diamond (square rotated 45°) in a given color
function drawDiamond(doc, cx, cy, size, fill) {
  const r = size / 2;
  doc.setFillColor(...fill);
  doc.triangle(cx - r, cy, cx, cy - r, cx + r, cy, 'F');
  doc.triangle(cx - r, cy, cx, cy + r, cx + r, cy, 'F');
}

// Page chrome (banner on page 1, footer on every page) is added at the end
function drawHeroBanner(doc, pageWidth) {
  const bannerH = 30;
  doc.setFillColor(...COLOR.ink);
  doc.rect(0, 0, pageWidth, bannerH, 'F');

  // Wordmark
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...COLOR.cream);
  const left = 16;
  doc.text('YAE  SERVICES', left, 14);

  // Orange diamond accent
  drawDiamond(doc, left + 38, 13, 3.2, COLOR.orange);

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('DPDP  READINESS  REPORT', left + 44, 14);

  // Right side: tagline mono caps
  doc.setFontSize(7);
  doc.setTextColor(245, 241, 234);
  doc.text('YAESERVICES.BIZ', pageWidth - 16, 14, { align: 'right' });

  // Thin orange rule under banner
  doc.setDrawColor(...COLOR.orange);
  doc.setLineWidth(0.6);
  doc.line(0, bannerH, pageWidth, bannerH);
}

function drawFooter(doc, pageNum, totalPages, pageWidth, pageHeight) {
  const y = pageHeight - 12;
  // Thin orange rule
  doc.setDrawColor(...COLOR.orange);
  doc.setLineWidth(0.4);
  doc.line(20, y - 4, pageWidth - 20, y - 4);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR.muted);
  doc.text('YAESERVICES.BIZ', 20, y);
  doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, y, { align: 'center' });
  doc.text('INFO@YAESERVICES.BIZ', pageWidth - 20, y, { align: 'right' });
  doc.setTextColor(...COLOR.black);
}

// Editorial section heading: mono-caps eyebrow + plum heading + orange underline rule
function drawSectionHeading(doc, eyebrow, title, x, y, pageWidth, margin) {
  const safeEyebrow = safe(eyebrow);
  const safeTitle = safe(title);

  // Eyebrow
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...COLOR.muted);
  doc.text(safeEyebrow.toUpperCase(), x, y);

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COLOR.plum);
  doc.text(safeTitle, x, y + 8);

  // Orange underline rule
  doc.setDrawColor(...COLOR.orange);
  doc.setLineWidth(0.5);
  const titleWidth = doc.getTextWidth(safeTitle);
  doc.line(x, y + 10.5, x + Math.max(titleWidth, 30), y + 10.5);

  // Reset
  doc.setTextColor(...COLOR.black);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  return y + 18; // new yPosition after heading
}

const tableHead = (head) => head;

const tableOptions = (yStart) => ({
  startY: yStart,
  theme: 'grid',
  headStyles: {
    fillColor: COLOR.plum,
    textColor: COLOR.cream,
    fontStyle: 'bold',
    fontSize: 8.5,
    halign: 'left',
    cellPadding: 4
  },
  bodyStyles: {
    textColor: COLOR.ink,
    fontSize: 8.5,
    cellPadding: 3.5,
    lineColor: COLOR.rule,
    lineWidth: 0.1
  },
  alternateRowStyles: { fillColor: COLOR.paper2 },
  styles: { font: 'helvetica' },
  margin: { left: 20, right: 20 }
});

export const generatePDFReport = (assessmentData, riskScore, riskLevel, recommendations) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;

  // Start below the hero banner
  let yPosition = 48;

  const checkPageBreak = (requiredSpace = 20) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      doc.addPage();
      yPosition = margin + 4;
      return true;
    }
    return false;
  };

  // Hero banner on page 1
  drawHeroBanner(doc, pageWidth);

  // ===== Title eyebrow + headline =====
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR.muted);
  doc.text('DPDP COMPLIANCE  ◆  RISK ASSESSMENT', margin, yPosition);
  yPosition += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...COLOR.ink);
  doc.text('DPDP Readiness & Risk Assessment', margin, yPosition);
  yPosition += 4;

  // Orange diamond + line
  doc.setDrawColor(...COLOR.orange);
  doc.setLineWidth(0.6);
  doc.line(margin, yPosition + 2, margin + 50, yPosition + 2);
  yPosition += 12;

  // Date line in mono caps
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLOR.muted);
  const date = new Date().toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  doc.text(`GENERATED  ${safe(date).toUpperCase()}`, margin, yPosition);
  yPosition += 10;

  // ===== Contact information block =====
  const company = assessmentData.companyName || assessmentData.organizationName;
  const phone =
    assessmentData.phone ||
    (assessmentData.phoneCountryCode && assessmentData.phoneNumber
      ? `${assessmentData.phoneCountryCode}${assessmentData.phoneNumber}`
      : null);

  if (company || assessmentData.name || assessmentData.email || phone) {
    // Hairline rule
    doc.setDrawColor(...COLOR.rule);
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 6;

    const fields = [
      ['COMPANY', company],
      ['CONTACT', assessmentData.name],
      ['EMAIL', assessmentData.email],
      ['PHONE', phone]
    ].filter((f) => !!f[1]);

    const colWidth = (pageWidth - margin * 2) / Math.min(fields.length, 4);

    fields.forEach((f, i) => {
      const x = margin + colWidth * i;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...COLOR.muted);
      doc.text(f[0], x, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...COLOR.ink);
      doc.text(safe(f[1]), x, yPosition + 6);
    });
    yPosition += 14;

    doc.setDrawColor(...COLOR.rule);
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 12;
  }

  // ===== Executive Summary =====
  checkPageBreak(40);
  yPosition = drawSectionHeading(
    doc,
    'Executive  ◆  Summary',
    'Risk score & level',
    margin,
    yPosition,
    pageWidth,
    margin
  );

  // Score (Fraunces-substitute: large helvetica light feel via size)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(48);
  doc.setTextColor(...COLOR.ink);
  doc.text(`${riskScore}`, margin, yPosition + 16);

  // Score label
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR.muted);
  doc.text('RISK  SCORE', margin, yPosition + 22);

  // Risk level pill (right side)
  const levelColor = riskColors[riskLevel] || COLOR.ink;
  const levelText = safe(riskLevel).toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  const lvlW = doc.getTextWidth(levelText);
  const pillX = pageWidth - margin - lvlW - 24;
  const pillY = yPosition + 6;
  doc.setFillColor(...levelColor);
  doc.roundedRect(pillX, pillY, lvlW + 18, 12, 6, 6, 'F');
  drawDiamond(doc, pillX + 7, pillY + 6, 3, COLOR.cream);
  doc.setTextColor(...COLOR.cream);
  doc.text(levelText, pillX + 13, pillY + 8);
  doc.setTextColor(...COLOR.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('RISK  LEVEL', pillX, pillY + 22);

  yPosition += 36;
  doc.setTextColor(...COLOR.black);

  // ===== Section A — Qualification Screening =====
  checkPageBreak(40);
  yPosition = drawSectionHeading(
    doc,
    'Section A  ◆  Qualification',
    'Qualification screening',
    margin,
    yPosition,
    pageWidth,
    margin
  );

  const sectionAData = [];
  const sectionAKeys = ['A1.1', 'A1.2', 'A1.3', 'A1.4', 'A1.5'];
  sectionAKeys.forEach((key) => {
    const value =
      assessmentData.sectionA && assessmentData.sectionA[key] !== undefined
        ? assessmentData.sectionA[key]
        : false;
    sectionAData.push([key, safe(getQuestionText(key)), formatAnswer(value)]);
  });

  doc.autoTable({
    ...tableOptions(yPosition),
    head: tableHead([['ID', 'Question', 'Answer']]),
    body: sectionAData,
    columnStyles: {
      0: { cellWidth: 22, fontStyle: 'bold' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 28, halign: 'center' }
    }
  });
  yPosition = doc.lastAutoTable.finalY + 14;

  // ===== Section B-J Answers =====
  if (assessmentData.responses && Object.keys(assessmentData.responses).length > 0) {
    checkPageBreak(30);
    yPosition = drawSectionHeading(
      doc,
      'Detailed Assessment  ◆  Sections B-J',
      'Detailed responses',
      margin,
      yPosition,
      pageWidth,
      margin
    );

    // Checkbox questions (B1.1 through B1.1.8)
    const checkboxKeys = ['B1.1', 'B1.2', 'B1.3', 'B1.4', 'B1.5', 'B1.6', 'B1.7', 'B1.1.8'];
    const checkboxResponses = checkboxKeys
      .filter((key) => assessmentData.responses[key] === true)
      .map((key) => [
        key,
        safe(getQuestionText(key)),
        formatAnswer(assessmentData.responses[key], true)
      ]);

    if (checkboxResponses.length > 0) {
      checkPageBreak(30);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...COLOR.plum);
      doc.text('Section B — Data Categories Collected', margin, yPosition);
      yPosition += 6;

      doc.autoTable({
        ...tableOptions(yPosition),
        head: tableHead([['ID', 'Data Category', 'Status']]),
        body: checkboxResponses,
        columnStyles: {
          0: { cellWidth: 22, fontStyle: 'bold' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 32, halign: 'center' }
        }
      });
      yPosition = doc.lastAutoTable.finalY + 12;
    }

    const sections = {
      B: 'Data Inventory & Processing Context',
      C: 'Lawful Purpose, Consent, Notices & Rights',
      D: 'Data Minimisation, Storage & Retention',
      E: 'Security Controls',
      F: 'Third-party & Vendor Management',
      G: 'Cross-border Data Transfers',
      H: 'Training & Awareness',
      I: 'Governance & Documentation',
      J: 'High-risk Sectors'
    };

    Object.keys(sections).forEach((sectionKey) => {
      const sectionResponses = Object.keys(assessmentData.responses)
        .filter((key) => {
          if (sectionKey === 'B' && checkboxKeys.includes(key)) return false;
          return key.startsWith(sectionKey);
        })
        .map((key) => [
          key,
          safe(getQuestionText(key)),
          formatAnswer(assessmentData.responses[key])
        ]);

      if (sectionResponses.length > 0) {
        checkPageBreak(30);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...COLOR.plum);
        doc.text(`Section ${sectionKey} — ${safe(sections[sectionKey])}`, margin, yPosition);
        yPosition += 6;

        doc.autoTable({
          ...tableOptions(yPosition),
          head: tableHead([['ID', 'Question', 'Answer']]),
          body: sectionResponses,
          columnStyles: {
            0: { cellWidth: 22, fontStyle: 'bold' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 28, halign: 'center' }
          }
        });
        yPosition = doc.lastAutoTable.finalY + 12;
      }
    });
  }

  // ===== Recommendations =====
  checkPageBreak(40);
  yPosition = drawSectionHeading(
    doc,
    'Next Steps  ◆  Action Plan',
    'Recommended actions',
    margin,
    yPosition,
    pageWidth,
    margin
  );

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...COLOR.ink);
  recommendations.forEach((rec) => {
    checkPageBreak(10);
    // Diamond bullet
    drawDiamond(doc, margin + 2, yPosition - 1.4, 2.4, COLOR.orange);
    const lines = doc.splitTextToSize(safe(rec), pageWidth - margin * 2 - 8);
    doc.text(lines, margin + 8, yPosition);
    yPosition += lines.length * 5 + 2;
  });

  // ===== Risk Classification Guide =====
  checkPageBreak(50);
  yPosition += 4;
  yPosition = drawSectionHeading(
    doc,
    'Reference  ◆  Risk Bands',
    'Risk classification',
    margin,
    yPosition,
    pageWidth,
    margin
  );

  const riskGuide = [
    { tag: '01  Low',      range: '0 - 20',  desc: 'Basic data, low volume, minimal SaaS reliance, strong controls.' },
    { tag: '02  Moderate', range: '21 - 40', desc: 'Customer-facing, collects IDs or financial data, moderate SaaS footprint, partial governance.' },
    { tag: '03  High',     range: '41 - 60', desc: 'Sensitive data, large volume, weak vendor controls, no policies in place.' },
    { tag: '04  Critical', range: '> 60',    desc: "Financial/healthcare, minors' data, cross-border transfers, weak controls, no DPO." }
  ];

  riskGuide.forEach((g) => {
    checkPageBreak(16);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...COLOR.orange);
    doc.text(g.tag.toUpperCase(), margin, yPosition);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLOR.ink);
    doc.text(g.range, margin + 36, yPosition);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLOR.muted);
    const lines = doc.splitTextToSize(safe(g.desc), pageWidth - margin * 2 - 80);
    doc.text(lines, margin + 70, yPosition);

    const blockH = Math.max(8, lines.length * 5 + 2);
    yPosition += blockH;

    // Hairline separator
    doc.setDrawColor(...COLOR.rule);
    doc.setLineWidth(0.2);
    doc.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);
  });

  doc.setTextColor(...COLOR.black);

  // ===== Footers on every page =====
  const totalPages = doc.internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFooter(doc, i, totalPages, pageWidth, pageHeight);
  }

  // Save
  const fileSafeName = (company || 'Organization').replace(/[^a-zA-Z0-9]+/g, '_').replace(/_+/g, '_');
  const fileName = `DPDP_Readiness_Report_${fileSafeName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
